/** Shared plan-catalog i18n helpers (billing namespace). */

export const normalizePlanId = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

export const planKeyFromId = (planId) => {
  const id = normalizePlanId(planId);
  return id === 'promax' ? 'proMax' : id;
};

export const catalogProductKey = (product) => {
  if (product === 'intelligence-navigator') return 'intelligence-navigator';
  if (product === 'autopilot') return 'autopilot';
  if (product === 'knowledge-iq' || product === 'data-iq') return 'standalone-iq';
  return product;
};

export const trialFeaturesProductKey = (product) => {
  if (product === 'data-iq' || product === 'knowledge-iq') return 'standalone-iq';
  if (product === 'intelligence-navigator') return 'intelligence-navigator';
  if (product === 'autopilot') return 'autopilot';
  return catalogProductKey(product);
};

export const resolveProductLabel = (t, product) =>
  t(`products.${product}`, { defaultValue: product });

export const resolvePlanName = (t, product, planId, fallbackName = '') => {
  const planKey = planKeyFromId(planId);
  const fromPlans = t(`subscriptionPage.plans.${planKey}`, { defaultValue: '' });
  if (fromPlans) return fromPlans;
  const fromCatalog = t(`subscriptionPage.planCatalog.${catalogProductKey(product)}.${planKey}.name`, { defaultValue: '' });
  return fromCatalog || fallbackName || planKey;
};

export const resolvePlanTagline = (t, product, planId, fallbackTagline = '', { trial = false } = {}) => {
  const planKey = planKeyFromId(planId);
  if (trial) {
    const trialTagline = t(`trial.fallbackTaglines.${catalogProductKey(product)}.${planKey}`, { defaultValue: '' });
    if (trialTagline) return trialTagline;
  }
  const fromCatalog = t(`subscriptionPage.planCatalog.${catalogProductKey(product)}.${planKey}.tagline`, { defaultValue: '' });
  return fromCatalog || fallbackTagline || t(`subscriptionPage.planCatalog.defaultTaglines.${normalizePlanId(planId)}`, '');
};

export const resolveCatalogFeatures = (t, product, planId) => {
  const planKey = planKeyFromId(planId);
  const features = t(`subscriptionPage.planCatalog.${catalogProductKey(product)}.${planKey}.features`, { returnObjects: true });
  return Array.isArray(features) ? features.map(String) : [];
};

export const resolveTrialPlanFeatures = (t, product, planId, iconResolver) => {
  const planKey = planKeyFromId(planId);
  const features = t(`trial.planFeatures.${trialFeaturesProductKey(product)}.${planKey}`, { returnObjects: true });
  if (!Array.isArray(features)) return [];
  return features.map((text) => ({
    icon: iconResolver(String(text)),
    text: String(text),
  }));
};

export const normalizePlanFeatureText = (t, product, text) => {
  const value = String(text || '').trim();
  const lower = value.toLowerCase();
  const normKeyByLower = {
    '5mb max upload': 'conversationCap100k',
    '5 mb max ai agents upload': 'conversationCap100k',
    '20mb max upload': 'conversationCap250k',
    '20 mb max ai agents upload': 'conversationCap250k',
    '50mb max upload': 'conversationCap500k',
    '50 mb max ai agents upload': 'conversationCap500k',
    '20k text processing': 'textLength20k',
    '20k text length': 'textLength20k',
    '100k text processing': 'textLength100k',
    '100k text length': 'textLength100k',
    '200k text processing': 'textLength200k',
    '200k text length': 'textLength200k',
  };
  if (normKeyByLower[lower]) {
    return t(`subscriptionPage.featureNormalization.${normKeyByLower[lower]}`);
  }
  if (product === 'intelligence-navigator' && lower.includes('ai tokens')) {
    const creditsLabel = t('subscriptionPage.featureNormalization.aiCredits');
    return value.replace(/AI tokens/gi, creditsLabel).replace(/ai tokens/g, creditsLabel);
  }
  return value;
};

const FALLBACK_PRICES = {
  autopilot: [
    { id: 'starter', monthly: 79.99, yearly: 767.90 },
    { id: 'professional', monthly: 199, yearly: 1910.40, recommended: true },
  ],
  'knowledge-iq': [
    { id: 'free', monthly: 0, yearly: 0, isFree: true },
    { id: 'starter', monthly: 39.99, yearly: 383.90 },
    { id: 'professional', monthly: 99, yearly: 950.40, recommended: true },
  ],
  'data-iq': [
    { id: 'free', monthly: 0, yearly: 0, isFree: true },
    { id: 'starter', monthly: 39.99, yearly: 383.90 },
    { id: 'professional', monthly: 99, yearly: 950.40, recommended: true },
  ],
  'intelligence-navigator': [
    { id: 'free', monthly: 0, yearly: 0, isFree: true },
    { id: 'starter', monthly: 11.99, yearly: 115.08 },
    { id: 'professional', monthly: 99, yearly: 950.40, recommended: true },
    { id: 'promax', monthly: 249, yearly: 2390.40 },
  ],
};

const UPGRADE_LIMITS = {
  'intelligence-navigator': {
    free: { monthlyCreditsLabel: '200K', analyticsMaxFileSizeLabel: '50 MB', analyticsMaxRowsLabel: '100K', maxFileSizeLabel: '5MB' },
    starter: { monthlyCreditsLabel: '4M', analyticsMaxFileSizeLabel: '50 MB', analyticsMaxRowsLabel: '100K', maxFileSizeLabel: '5MB' },
    professional: { monthlyCreditsLabel: '9M', analyticsMaxFileSizeLabel: '500 MB', analyticsMaxRowsLabel: '100K', maxFileSizeLabel: '20MB' },
    promax: { monthlyCreditsLabel: '20M', analyticsMaxFileSizeLabel: '1 GB', analyticsMaxRowsLabel: '500K', maxFileSizeLabel: '50MB' },
  },
};

export const buildTrialFallbackPlans = (t, product) => {
  const rows = FALLBACK_PRICES[product] || FALLBACK_PRICES['intelligence-navigator'];
  return rows.map((row) => ({
    ...row,
    name: resolvePlanName(t, product, row.id),
    tagline: resolvePlanTagline(t, product, row.id, '', { trial: true }),
  }));
};

export const buildUpgradeFallbackPlans = (t, product) => {
  const rows = FALLBACK_PRICES[product] || FALLBACK_PRICES['knowledge-iq'];
  const limitsForProduct = UPGRADE_LIMITS[product] || {};
  return rows.map((row) => ({
    ...row,
    name: resolvePlanName(t, product, row.id),
    tagline: resolvePlanTagline(t, product, row.id),
    limits: limitsForProduct[row.id] || {},
    features: resolveCatalogFeatures(t, product, row.id).map((text) =>
      normalizePlanFeatureText(t, product, text)
    ),
  }));
};

export const formatVerificationError = (t, { statusCode, reasons = [] }) => {
  if (statusCode > 0) {
    return t('trial.verificationError.status', { status: statusCode });
  }
  const parts = reasons.filter(Boolean);
  if (parts.length === 1) {
    return t('trial.verificationError.reason', { reason: parts[0] });
  }
  if (parts.length > 1) {
    return t('trial.verificationError.compound', { a: parts[0], b: parts[1], c: parts[2] || '' });
  }
  return t('trial.verificationError.reason', { reason: t('trial.errorReasons.unknown') });
};

export const describeVerificationFailure = (t, err) => {
  const statusCode = Number(err?.statusCode || err?.response?.status || 0);
  if (statusCode > 0) return t('trial.errorReasons.statusCode', { code: statusCode });
  const message = String(err?.message || '').toLowerCase();
  if (message.includes('timeout')) return t('trial.errorReasons.timeout');
  if (message.includes('failed to fetch')) return t('trial.errorReasons.network');
  return t('trial.errorReasons.unknown');
};
